import AddIcon from '@mui/icons-material/Add';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import BlockIcon from '@mui/icons-material/Block';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { GridToolbarContainer } from '@mui/x-data-grid';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useState } from 'react';
import { getLpSuppliers } from '../../../../features/master-data';
import { StorageKey } from '../../../../shared/auth/DataService';
import ExpSnackbarAlerts from '../../../../shared/components/display/ExpSnackbarAlerts';
import DoubleConfirmModal from '../../../../shared/components/modals/DoubleConfirmModal';
import MBModal from '../../../../shared/components/modals/MBModal';
import useSnackbar from '../../../../shared/hooks/useSnackbar';
import {
  createApiKey,
  getApiKeys,
  getApiScopes,
  revokeApiKey,
  rotateApiKey,
  updateApiKeyStagingMode,
} from '../../services/SettingsRepository';
import { getStagingModeChipProps } from '../../types/apiKeyConstants';
import CreateApiKeyDialog from '../dialogs/CreateApiKeyDialog';
import EditStagingModeDialog from '../dialogs/EditStagingModeDialog';

const ApiKeyTable = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [availableScopes, setAvailableScopes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialog state
  const [createOpen, setCreateOpen] = useState(false);

  // Edit staging mode dialog
  const [editStagingOpen, setEditStagingOpen] = useState(false);
  const [editStagingKey, setEditStagingKey] = useState(null);

  // Confirm revoke dialog
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [keyToRevoke, setKeyToRevoke] = useState(null);

  // Confirm rotate dialog
  const [rotateOpen, setRotateOpen] = useState(false);
  const [keyToRotate, setKeyToRotate] = useState(null);
  const [rotatedKeyResult, setRotatedKeyResult] = useState(null);

  const { showSnackbar, snackbarProps } = useSnackbar();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [keysResp, suppliersResp, scopesResp] = await Promise.all([
        getApiKeys(),
        getLpSuppliers(),
        getApiScopes(),
      ]);
      setApiKeys(keysResp.map((k, i) => ({ ...k, rowId: i + 1 })));
      setSuppliers(suppliersResp);
      setAvailableScopes(scopesResp);
    } catch (error) {
      showSnackbar('Error fetching API keys.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ───────────── Create ─────────────
  const handleCreateOpen = () => setCreateOpen(true);

  const handleCreateConfirm = async ({
    keyType,
    selectedSupplier,
    label,
    selectedScopes,
    stagingMode,
  }) => {
    if (keyType === 'SUPPLIER_BOUND' && !selectedSupplier) return null;
    if (keyType === 'INTERNAL_UNRESTRICTED' && selectedScopes.length === 0)
      return null;
    try {
      const createdBy = localStorage.getItem(StorageKey.userInfo_sub) || '';
      const payload = {
        keyType,
        label:
          label ||
          (keyType === 'INTERNAL_UNRESTRICTED'
            ? 'Internal key'
            : `Key for ${selectedSupplier.supplierName}`),
        createdBy,
        stagingMode,
      };
      if (keyType === 'SUPPLIER_BOUND') {
        payload.supplierNo = selectedSupplier.supplierNo;
      } else {
        payload.scopes = selectedScopes.join(',');
      }
      const result = await createApiKey(payload);
      await fetchData();
      showSnackbar('API key created successfully!');
      return result;
    } catch (error) {
      showSnackbar('Error creating API key.', 'error');
      return null;
    }
  };

  // ───────────── Revoke ─────────────
  const handleRevokeClick = (row) => {
    setKeyToRevoke(row);
    setRevokeOpen(true);
  };

  const handleRevokeConfirm = async () => {
    try {
      await revokeApiKey(keyToRevoke.id);
      await fetchData();
      showSnackbar('API key revoked.');
    } catch (error) {
      showSnackbar('Error revoking API key.', 'error');
    } finally {
      setRevokeOpen(false);
      setKeyToRevoke(null);
    }
  };

  // ───────────── Rotate ─────────────
  const handleRotateClick = (row) => {
    setKeyToRotate(row);
    setRotatedKeyResult(null);
    setRotateOpen(true);
  };

  const handleRotateConfirm = async () => {
    try {
      const createdBy = localStorage.getItem(StorageKey.userInfo_sub) || '';
      const result = await rotateApiKey(keyToRotate.id, { createdBy });
      setRotateOpen(false);
      setRotatedKeyResult(result);
      await fetchData();
      showSnackbar('API key rotated successfully!');
    } catch (error) {
      showSnackbar('Error rotating API key.', 'error');
    }
  };

  // ───────────── Copy to clipboard ─────────────
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showSnackbar('Key copied to clipboard.', 'info');
  };

  // ───────────── Edit Staging Mode ─────────────
  const handleEditStagingClick = (row) => {
    setEditStagingKey(row);
    setEditStagingOpen(true);
  };

  const handleEditStagingConfirm = async (keyId, newMode) => {
    try {
      await updateApiKeyStagingMode(keyId, newMode);
      await fetchData();
      showSnackbar('Staging mode updated.');
    } catch (error) {
      showSnackbar('Error updating staging mode.', 'error');
    }
  };

  // ───────────── Columns ─────────────
  const columns = [
    {
      field: 'keyPrefix',
      headerName: 'Key Prefix',
      width: 130,
      renderCell: (params) => (
        <Typography variant='body2' fontFamily='monospace'>
          {params.value}…
        </Typography>
      ),
    },
    { field: 'label', headerName: 'Label', width: 220 },
    {
      field: 'keyType',
      headerName: 'Type',
      width: 150,
      renderCell: (params) => (
        <Chip
          label={
            params.value === 'INTERNAL_UNRESTRICTED' ? 'Internal' : 'Supplier'
          }
          color={params.value === 'INTERNAL_UNRESTRICTED' ? 'info' : 'default'}
          size='small'
          variant='outlined'
        />
      ),
    },
    { field: 'supplierNo', headerName: 'Supplier No', width: 120 },
    { field: 'supplierName', headerName: 'Supplier', width: 180 },
    {
      field: 'scopes',
      headerName: 'Scopes',
      width: 220,
      renderCell: (params) => {
        if (!params.value) return null;
        return params.value
          .split(',')
          .map((s) => <Chip key={s} label={s} size='small' sx={{ mr: 0.5 }} />);
      },
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 110,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Revoked'}
          color={params.value ? 'success' : 'default'}
          size='small'
        />
      ),
    },
    { field: 'createdAt', headerName: 'Created', width: 170 },
    { field: 'expiresAt', headerName: 'Expires', width: 170 },
    { field: 'lastUsedAt', headerName: 'Last Used', width: 170 },
    { field: 'createdBy', headerName: 'Created By', width: 140 },
    {
      field: 'stagingMode',
      headerName: 'Staging',
      width: 130,
      renderCell: (params) => {
        const chipProps = getStagingModeChipProps(params.value);
        return (
          <Chip
            label={chipProps.label}
            color={chipProps.color}
            size='small'
            variant={chipProps.variant}
          />
        );
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 160,
      getActions: (params) => {
        const row = params.row;
        if (!row.isActive) return [];
        return [
          <Tooltip title='Edit Staging Mode' key='staging'>
            <IconButton
              size='small'
              color='default'
              onClick={() => handleEditStagingClick(row)}
            >
              <EditIcon fontSize='small' />
            </IconButton>
          </Tooltip>,
          <Tooltip title='Revoke' key='revoke'>
            <IconButton
              size='small'
              color='error'
              onClick={() => handleRevokeClick(row)}
            >
              <BlockIcon fontSize='small' />
            </IconButton>
          </Tooltip>,
          <Tooltip title='Rotate' key='rotate'>
            <IconButton
              size='small'
              color='primary'
              onClick={() => handleRotateClick(row)}
            >
              <AutorenewIcon fontSize='small' />
            </IconButton>
          </Tooltip>,
        ];
      },
    },
  ];

  return (
    <Box height='55vh'>
      <DataGridPro
        loading={loading}
        pagination
        rows={apiKeys}
        columns={columns}
        getRowId={(row) => row.id}
        pageSizeOptions={[10, 25, 50]}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
        }}
        slots={{
          toolbar: () => (
            <GridToolbarContainer>
              <Button
                color='primary'
                startIcon={<AddIcon />}
                onClick={handleCreateOpen}
              >
                Create API Key
              </Button>
            </GridToolbarContainer>
          ),
        }}
      />

      {/* ──── Create Dialog ──── */}
      <CreateApiKeyDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        suppliers={suppliers}
        availableScopes={availableScopes}
        onConfirm={handleCreateConfirm}
        onCopyKey={copyToClipboard}
      />

      {/* ──── Revoke Confirm ──── */}
      <DoubleConfirmModal
        open={revokeOpen}
        title='Revoke API Key'
        confirmText='Revoke'
        onConfirm={handleRevokeConfirm}
        onCancel={() => setRevokeOpen(false)}
        onClose={() => setRevokeOpen(false)}
        contextText={
          <>
            Are you sure you want to revoke key <b>{keyToRevoke?.keyPrefix}…</b>{' '}
            for supplier <b>{keyToRevoke?.supplierName}</b>? This cannot be
            undone.
          </>
        }
      />

      {/* ──── Rotate Confirm ──── */}
      <DoubleConfirmModal
        open={rotateOpen}
        title='Rotate API Key'
        confirmText='Rotate'
        onConfirm={handleRotateConfirm}
        onCancel={() => setRotateOpen(false)}
        onClose={() => setRotateOpen(false)}
        contextText={
          <>
            This will revoke key <b>{keyToRotate?.keyPrefix}…</b> and create a
            new key for supplier <b>{keyToRotate?.supplierName}</b>. Continue?
          </>
        }
      />

      {/* ──── Rotate Result ──── */}
      <MBModal
        open={!!rotatedKeyResult}
        title='New API Key'
        onClose={() => {
          setRotatedKeyResult(null);
          setKeyToRotate(null);
        }}
        actions={
          <Button
            variant='contained'
            onClick={() => {
              setRotatedKeyResult(null);
              setKeyToRotate(null);
            }}
          >
            Done
          </Button>
        }
      >
        <Alert severity='warning' sx={{ mb: 2 }}>
          Copy this new key now — it will <b>not</b> be shown again. The old key
          has been revoked.
        </Alert>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            bgcolor: (theme) =>
              theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
            p: 2,
            borderRadius: 1,
            fontFamily: 'monospace',
            wordBreak: 'break-all',
          }}
        >
          <Typography
            variant='body2'
            fontFamily='monospace'
            sx={{ flexGrow: 1 }}
          >
            {rotatedKeyResult?.rawKey}
          </Typography>
          <IconButton
            size='small'
            onClick={() => copyToClipboard(rotatedKeyResult?.rawKey)}
          >
            <ContentCopyIcon fontSize='small' />
          </IconButton>
        </Box>
      </MBModal>

      {/* ──── Edit Staging Mode ──── */}
      <EditStagingModeDialog
        open={editStagingOpen}
        onClose={() => {
          setEditStagingOpen(false);
          setEditStagingKey(null);
        }}
        apiKey={editStagingKey}
        onConfirm={handleEditStagingConfirm}
      />

      <ExpSnackbarAlerts {...snackbarProps} />
    </Box>
  );
};

export default ApiKeyTable;
